import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { ApiError, onUnauthorized } from '../lib/api.js';
import { SESSION_KEY, useSession } from '../lib/session.js';
import { Skeleton } from './ui/States.js';

/**
 * Session gate. Bootstraps `GET /auth/me`, sends unauthenticated visitors to
 * `/login` with a return path, and listens for any later 401 so an expired
 * session redirects wherever it happens.
 */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();

  useEffect(
    () =>
      onUnauthorized(() => {
        if (location.pathname === '/login') return;
        // An expired session must not stay cached as signed in, or the login
        // page bounces straight back here and the two loop.
        client.removeQueries({ queryKey: SESSION_KEY });
        void navigate(
          `/login?return_to=${encodeURIComponent(location.pathname + location.search)}`,
          { replace: true },
        );
      }),
    [client, navigate, location.pathname, location.search],
  );

  if (session.isPending) {
    return (
      <div className="flex min-h-screen flex-col gap-3 bg-[var(--canvas)] p-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (session.error instanceof ApiError && session.error.status === 401) {
    return (
      <Navigate
        to={`/login?return_to=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  return <Outlet />;
}
